/**
 * scholarUtils.js
 * Google Scholar & Academic Metadata Utilities for ScienceXplorer
 * Implements Highwire Press, Dublin Core, Schema.org ScholarlyArticle,
 * and Author Indexing Mechanisms.
 */

export function cleanAuthorName(raw) {
  if (!raw) return "";
  let s = String(raw)
    .replace(/[\u00B9\u00B2\u00B3\u2070-\u2079*†‡]/g, "") // remove footnotes & asterisks
    .replace(/^\s*\d+\s*[.)]?\s*/, "") // remove leading numbering
    .replace(/\s*\d+\s*$/, "") // remove trailing numbering
    .replace(/\[.*?\]/g, "") // remove brackets like [Lead Author]
    .replace(/\s+/g, " ")
    .trim();

  // If in "Lastname, Firstname Middle" format without extra commas, convert to "Firstname Middle Lastname"
  if (s.includes(",")) {
    const parts = s.split(",").map((p) => p.trim());
    if (parts.length === 2 && !parts[0].includes(" ") && parts[1].length > 0) {
      return `${parts[1]} ${parts[0]}`.trim();
    }
  }
  return s;
}

export function parseAuthors(raw) {
  if (!raw) return [];
  const cleaned = String(raw).replace(/[\u00B9\u00B2\u00B3\u2070-\u2079*†‡]/g, "");

  // 1. Semicolon separated (e.g. "Ishola, Oluwatoyin; Festus, Oluwaloni Olukoya")
  if (cleaned.includes(";")) {
    return cleaned
      .split(";")
      .map((p) => cleanAuthorName(p))
      .filter((p) => p.length > 1);
  }

  // 2. Single author formatted as "Lastname, Firstname Middle" (has exactly 1 comma and first part is single word)
  const commaCount = (cleaned.match(/,/g) || []).length;
  if (commaCount === 1) {
    const [part1, part2] = cleaned.split(",").map((s) => s.trim());
    if (!part1.includes(" ") && part2.length > 1) {
      return [cleanAuthorName(`${part2} ${part1}`)];
    }
  }

  // 3. " and " separated or comma separated list
  let str = cleaned;
  if (/\band\b/i.test(str)) {
    str = str.replace(/\s+and\s+/gi, ", ");
  }

  return str
    .split(",")
    .map((p) => cleanAuthorName(p))
    .filter((p) => p.length > 1);
}

export function getGoogleScholarAuthorUrl(authorName) {
  if (!authorName) return "https://scholar.google.com";
  const clean = cleanAuthorName(authorName);
  return `https://scholar.google.com/scholar?q=author:"${encodeURIComponent(clean)}"`;
}

export function getGoogleScholarAuthorProfileUrl(authorName) {
  if (!authorName) return "https://scholar.google.com/citations";
  const clean = cleanAuthorName(authorName);
  return `https://scholar.google.com/citations?view_op=search_authors&mauthors=${encodeURIComponent(clean)}`;
}

export function getGoogleScholarJournalAuthorUrl(authorName) {
  if (!authorName) return "https://scholar.google.com";
  const clean = cleanAuthorName(authorName);
  return `https://scholar.google.com/scholar?q=author:"${encodeURIComponent(clean)}"+ScienceXplorer`;
}

export function getGoogleScholarArticleUrl(title, doi) {
  if (doi && String(doi).trim()) {
    const cleanDoi = String(doi).replace(/^https?:\/\/(?:dx\.)?doi\.org\//i, "").trim();
    return `https://scholar.google.com/scholar?q="${encodeURIComponent(cleanDoi)}"`;
  }
  if (!title) return "https://scholar.google.com";
  const cleanTitle = title.replace(/[^\w\s-]/g, " ").replace(/\s+/g, " ").trim();
  return `https://scholar.google.com/scholar?q=allintitle:"${encodeURIComponent(cleanTitle)}"`;
}

export function resolvePdfUrl(rawUrl, baseOrigin) {
  if (!rawUrl || typeof rawUrl !== "string") return "";
  const trimmed = rawUrl.trim();
  if (trimmed.startsWith("data:")) return ""; // Data URIs are inline binaries, not crawlable HTTP links
  if (trimmed.startsWith("http://") || trimmed.startsWith("https://")) {
    return trimmed.replace(/^https?:\/\/sciencexplorer\.io/i, baseOrigin);
  }
  if (trimmed.startsWith("/")) {
    return `${baseOrigin}${trimmed}`;
  }
  return `${baseOrigin}/${trimmed}`;
}

/**
 * Generates Highwire Press and Dublin Core tags for an article
 */
export function generateHighwireTags(article, origin = "") {
  if (!article) return [];
  const baseOrigin = origin || (typeof window !== "undefined" && window.location.origin && !window.location.origin.includes("localhost") ? window.location.origin : "https://www.thesciencexplorer.org");
  const year = article.published_at ? new Date(article.published_at).getFullYear() : (article.pub_year || "");
  const pubDate = article.published_at ? article.published_at.slice(0, 10) : (year ? `${year}-01-01` : "");

  let articleDoi = "";
  if (article.doi && String(article.doi).trim()) {
    articleDoi = String(article.doi).replace(/^https?:\/\/(?:dx\.)?doi\.org\//i, "").trim();
  }

  const authors = parseAuthors(article.authors);
  const pdfFullUrl = resolvePdfUrl(article.pdf_url, baseOrigin);
  const canonicalUrl = `${baseOrigin}/article/${article.id}`;

  const tags = [
    { name: "citation_title", content: article.title },
    { name: "citation_journal_title", content: "ScienceXplorer" },
    { name: "citation_publisher", content: "ScienceXplorer Press" },
    { name: "citation_issn", content: "2995-1234" },
    { name: "citation_language", content: "en" },
    { name: "citation_publication_date", content: pubDate },
    { name: "citation_year", content: String(year) },
    ...(article.volume ? [{ name: "citation_volume", content: String(article.volume) }] : []),
    ...(article.issue ? [{ name: "citation_issue", content: String(article.issue) }] : []),
    ...(articleDoi ? [{ name: "citation_doi", content: articleDoi }] : []),
    { name: "citation_abstract_html_url", content: canonicalUrl },
    ...(pdfFullUrl ? [{ name: "citation_pdf_url", content: pdfFullUrl }] : []),
    ...authors.map((a) => ({ name: "citation_author", content: a })),
    ...(article.affiliations ? [{ name: "citation_author_institution", content: article.affiliations }] : []),
    ...(article.keywords ? [{ name: "citation_keywords", content: article.keywords }] : []),
    // Dublin Core
    { name: "DC.title", content: article.title },
    { name: "DC.publisher", content: "ScienceXplorer Press" },
    { name: "DC.date", content: pubDate },
    { name: "DC.source", content: "ScienceXplorer" },
    { name: "DC.identifier", content: articleDoi ? `https://doi.org/${articleDoi}` : canonicalUrl },
    { name: "DC.type", content: "Journal Article" },
    { name: "DC.format", content: "text/html" },
    { name: "DC.language", content: "en" },
    ...(article.category ? [{ name: "DC.subject", content: article.category }] : []),
    ...authors.map((a) => ({ name: "DC.creator", content: a })),
  ];

  return tags.filter((t) => t.content && String(t.content).trim());
}

/**
 * Generates Schema.org ScholarlyArticle JSON-LD
 */
export function generateScholarlyJsonLd(article, origin = "") {
  if (!article) return null;
  const baseOrigin = origin || (typeof window !== "undefined" && window.location.origin && !window.location.origin.includes("localhost") ? window.location.origin : "https://www.thesciencexplorer.org");
  const authors = parseAuthors(article.authors);
  const year = article.published_at ? new Date(article.published_at).getFullYear() : (article.pub_year || "");
  const pubDate = article.published_at ? article.published_at.slice(0, 10) : (year ? `${year}-01-01` : "");
  let articleDoi = "";
  if (article.doi && String(article.doi).trim()) {
    articleDoi = String(article.doi).replace(/^https?:\/\/(?:dx\.)?doi\.org\//i, "").trim();
  }

  const pdfFullUrl = resolvePdfUrl(article.pdf_url, baseOrigin);

  return {
    "@context": "https://schema.org",
    "@type": "ScholarlyArticle",
    "headline": article.title,
    "name": article.title,
    "description": article.abstract || "",
    "inLanguage": "en",
    "datePublished": pubDate,
    "isPartOf": {
      "@type": "Periodical",
      "name": "ScienceXplorer",
      "issn": "2995-1234",
      "publisher": {
        "@type": "Organization",
        "name": "ScienceXplorer Press",
        "url": baseOrigin
      }
    },
    ...(article.volume ? { "volumeNumber": String(article.volume) } : {}),
    ...(article.issue ? { "issueNumber": String(article.issue) } : {}),
    ...(articleDoi ? { "identifier": `https://doi.org/${articleDoi}`, "sameAs": `https://doi.org/${articleDoi}` } : {}),
    "author": authors.map((a) => ({
      "@type": "Person",
      "name": a,
      ...(article.affiliations ? { "affiliation": { "@type": "Organization", "name": article.affiliations } } : {})
    })),
    "publisher": {
      "@type": "Organization",
      "name": "ScienceXplorer Press",
      "url": baseOrigin
    },
    "url": `${baseOrigin}/article/${article.id}`,
    ...(pdfFullUrl ? { "encoding": [{ "@type": "MediaObject", "encodingFormat": "application/pdf", "contentUrl": pdfFullUrl }] } : {}),
    "license": "https://creativecommons.org/licenses/by/4.0/"
  };
}

export function generateBibTeX(article, origin = "") {
  if (!article) return "";
  const baseOrigin = origin || (typeof window !== "undefined" && window.location.origin && !window.location.origin.includes("localhost") ? window.location.origin : "https://www.thesciencexplorer.org");
  const authors = parseAuthors(article.authors).join(" and ");
  const year = article.published_at ? new Date(article.published_at).getFullYear() : (article.pub_year || "2026");
  let doiLine = "";
  if (article.doi && String(article.doi).trim()) {
    const d = String(article.doi).replace(/^https?:\/\/(?:dx\.)?doi\.org\//i, "").trim();
    doiLine = `,\n  doi       = {${d}}`;
  }
  return `@article{sciencexplorer_${article.id},
  title     = {${article.title}},
  author    = {${authors || "Author, A."}},
  journal   = {ScienceXplorer},
  year      = {${year}},
  volume    = {${article.volume || "1"}},
  number    = {${article.issue || "1"}},
  url       = {${baseOrigin}/article/${article.id}}${doiLine}
}`;
}
